/**
 * Lista de ajustes de un reporte: crear, completar, validar y reabrir.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ink, studio, FilterPill } from './studioUi';
import { useAuth } from '../../AuthContext';
import { useIsNarrow } from '../../useMediaQuery';
import EditIcon from '../../assets/icons/edit.svg';
import TrashIcon from '../../assets/icons/trash.svg';
import SendIcon from '../../assets/icons/send.svg';
import UserCheckIcon from '../../assets/icons/user-check.svg';
import ReplyIcon from '../../assets/icons/reply.svg';

const looksLikeUid = (value) => {
    const text = String(value || '').trim();
    return text.length >= 20 && /^[A-Za-z0-9_-]+$/.test(text);
};

const personName = (person) => {
    const raw = String(person?.name || '').trim();
    if (!raw || looksLikeUid(raw)) return 'Usuario';
    return raw;
};

const personMeta = (person) => {
    const area = String(person?.area || '').trim();
    return area || 'Sin área';
};

const iconImg = { width: 15, height: 15, display: 'block' };

const IconAction = ({ title, onClick, danger, disabled, children }) => {
    const narrow = useIsNarrow();
    return (
    <button
        type="button"
        title={title}
        aria-label={title}
        disabled={disabled}
        onClick={onClick}
        style={{
            ...(danger ? studio.iconBtnDanger : studio.iconBtn),
            marginRight: 0,
            width: narrow ? 38 : 28,
            height: narrow ? 38 : 28,
            opacity: disabled ? 0.45 : 1,
        }}
    >
        {children}
    </button>
    );
};

const CheckIcon = () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M20 6L9 17L4 12" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);
const STATUS_META = {
    pending: { label: 'Pendiente', color: '#e65100', backgroundColor: '#fff3e0' },
    completed: { label: 'Por validar', color: ink.accentText, backgroundColor: ink.accentSoft },
    validated: { label: 'Validado', color: '#137333', backgroundColor: '#e6f4ea' },
};

const sameArea = (a, b) => String(a || '').trim().toLowerCase() === String(b || '').trim().toLowerCase() && String(a || '').trim();

const STALE_MS = 3 * 24 * 60 * 60 * 1000;
const TEXT_LIMIT = 150;

const toMillis = (value) => {
    if (!value) return 0;
    if (typeof value.toDate === 'function') return value.toDate().getTime();
    if (value._seconds != null) return value._seconds * 1000;
    const parsed = new Date(value).getTime();
    return Number.isFinite(parsed) ? parsed : 0;
};

const uniqueResponsibles = (rows = []) => {
    const seen = new Set();
    return rows.filter((row) => {
        const id = row.userId;
        if (!id || seen.has(id)) return false;
        seen.add(id);
        return true;
    });
};

const responsibleLabel = (row) => {
    const name = row.userDetails?.nombre || row.nombre || 'Responsable';
    const area = row.area || row.userDetails?.area || '';
    return area ? `${name} · ${area}` : name;
};

const AdjustmentBody = ({ text, expanded, onToggle }) => {
    const raw = String(text || '');
    const long = raw.length > TEXT_LIMIT;
    const shown = !long || expanded ? raw : raw.slice(0, TEXT_LIMIT).trimEnd();
    return (
        <p style={{ margin: '8px 0 0', whiteSpace: 'pre-wrap', color: ink.text, lineHeight: 1.4, fontSize: '0.92rem' }}>
            {shown}
            {long && !expanded ? (
                <button
                    type="button"
                    onClick={onToggle}
                    title="Ver texto completo"
                    style={expandBtn}
                >
                    …
                </button>
            ) : null}
            {long && expanded ? (
                <>
                    {' '}
                    <button type="button" onClick={onToggle} style={expandBtn}>
                        ver menos
                    </button>
                </>
            ) : null}
        </p>
    );
};

const expandBtn = {
    appearance: 'none',
    border: 'none',
    backgroundColor: 'transparent',
    color: ink.accentText,
    fontWeight: 700,
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: '0.92rem',
    padding: '0 0 0 2px',
    margin: 0,
};

const ReportAdjustmentsPanel = ({
    reportId,
    reportResponsibles = [],
    currentUser,
    userRole,
    userPermissions,
    fetchWithAuth,
    API_URL,
    refreshKey,
    onCountsChange,
    onItemsChange,
}) => {
    const { withBusy } = useAuth();
    const narrow = useIsNarrow();
    /* En móvil: inputs a 16px (evita el zoom de iOS) y sin anchos mínimos fijos. */
    const control = narrow ? { ...studio.control, fontSize: 16 } : studio.control;
    const [items, setItems] = useState([]);
    const [filter, setFilter] = useState('all');
    const [draft, setDraft] = useState('');
    const [busy, setBusy] = useState('');
    const [error, setError] = useState('');
    const [editingId, setEditingId] = useState(null);
    const [editText, setEditText] = useState('');
    const [reopenId, setReopenId] = useState(null);
    const [reopenNote, setReopenNote] = useState('');
    const [assigneeId, setAssigneeId] = useState('');
    const [expandedIds, setExpandedIds] = useState(() => new Set());

    const canManage = userRole === 'admin' || userPermissions.reports_manage_adjustments === true;
    const assigneeOptions = useMemo(() => uniqueResponsibles(reportResponsibles), [reportResponsibles]);
    const canCompleteItem = (item) => canManage || item.assignedTo?.uid === currentUser?.uid;
    const canAssignItem = (item) => canManage || item.requestedBy?.uid === currentUser?.uid;
    const toggleExpanded = (id) => {
        setExpandedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const canValidateItem = useCallback((item) => {
        if (canManage) return true;
        if (item.requestedBy?.uid === currentUser?.uid) return true;
        const mine = reportResponsibles.filter((row) => row.userId === currentUser?.uid);
        if (!mine.length) return false;
        if (userRole === 'pm') return true;
        return mine.some((row) => sameArea(row.area, item.requestedBy?.area));
    }, [canManage, currentUser?.uid, reportResponsibles, userRole]);

    const load = useCallback(async () => {
        if (!reportId) return;
        try {
            const res = await fetchWithAuth(`${API_URL}/admin/reportes-maestros/${reportId}/adjustments`);
            const data = await res.json();
            setItems(data.adjustments || []);
        } catch (err) {
            setError(err.message || 'No se pudieron cargar los ajustes.');
        }
    }, [API_URL, fetchWithAuth, reportId]);

    useEffect(() => { load(); }, [load, refreshKey]);
    useEffect(() => { if (onItemsChange) onItemsChange(items); }, [items, onItemsChange]);

    const run = async (key, fn) => {
        setBusy(key);
        setError('');
        try {
            await withBusy(async () => {
                await fn();
                await load();
            }, 'Guardando ajuste…');
        } catch (err) {
            setError(err.message || 'No se pudo completar la acción.');
        } finally {
            setBusy('');
        }
    };

    const create = () => run('create', async () => {
        const text = draft.trim();
        if (!text) throw new Error('Escribe el ajuste.');
        if (!assigneeId) throw new Error('Asigna el ajuste a un responsable.');
        const res = await fetchWithAuth(`${API_URL}/admin/reportes-maestros/${reportId}/adjustments`, {
            method: 'POST',
            body: JSON.stringify({ text, assignedToUserId: assigneeId }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'No se pudo crear el ajuste.');
        setDraft('');
        if (data.adjustmentCounts && onCountsChange) onCountsChange(data.adjustmentCounts);
    });

    const reassign = (id, nextUid) => {
        if (!nextUid) return;
        run(`a-${id}`, async () => {
            const res = await fetchWithAuth(`${API_URL}/admin/reportes-maestros/${reportId}/adjustments/${id}/assign`, {
                method: 'POST',
                body: JSON.stringify({ assignedToUserId: nextUid }),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || 'No se pudo reasignar.');
        });
    };

    const complete = (id) => run(`c-${id}`, () => fetchWithAuth(`${API_URL}/admin/reportes-maestros/${reportId}/adjustments/${id}/complete`, { method: 'POST' }).then((res) => res.json()).then((data) => {
        if (data.adjustmentCounts && onCountsChange) onCountsChange(data.adjustmentCounts);
    }));

    const validate = (id) => run(`v-${id}`, () => fetchWithAuth(`${API_URL}/admin/reportes-maestros/${reportId}/adjustments/${id}/validate`, { method: 'POST' }).then((res) => res.json()).then((data) => {
        if (data.adjustmentCounts && onCountsChange) onCountsChange(data.adjustmentCounts);
    }));

    const reopen = (id) => run(`r-${id}`, async () => {
        const res = await fetchWithAuth(`${API_URL}/admin/reportes-maestros/${reportId}/adjustments/${id}/reopen`, {
            method: 'POST',
            body: JSON.stringify({ note: reopenNote }),
        });
        const data = await res.json();
        setReopenId(null);
        setReopenNote('');
        if (data.adjustmentCounts && onCountsChange) onCountsChange(data.adjustmentCounts);
    });

    const saveEdit = (id) => run(`e-${id}`, async () => {
        await fetchWithAuth(`${API_URL}/admin/reportes-maestros/${reportId}/adjustments/${id}`, {
            method: 'PATCH',
            body: JSON.stringify({ text: editText }),
        });
        setEditingId(null);
    });

    const remove = (id) => {
        if (!window.confirm('¿Eliminar este ajuste?')) return;
        run(`d-${id}`, () => fetchWithAuth(`${API_URL}/admin/reportes-maestros/${reportId}/adjustments/${id}`, { method: 'DELETE' }).then((res) => res.json()).then((data) => {
            if (data.adjustmentCounts && onCountsChange) onCountsChange(data.adjustmentCounts);
        }));
    };

    const visible = useMemo(() => {
        if (filter === 'mine') return items.filter((item) => item.assignedTo?.uid === currentUser?.uid);
        return filter === 'all' ? items : items.filter((item) => item.status === filter);
    }, [filter, items, currentUser?.uid]);

    const counts = useMemo(() => {
        const next = { pending: 0, completed: 0, validated: 0, total: items.length };
        items.forEach((item) => {
            if (next[item.status] != null) next[item.status] += 1;
        });
        return next;
    }, [items]);

    return (
        <div style={studio.card}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
                <div>
                    <p style={studio.kicker}>Control</p>
                    <h3 style={{ ...studio.formTitle, margin: '4px 0 0' }}>Ajustes · {counts.total}</h3>
                </div>
                <div style={studio.stats}>
                    <div style={{ ...studio.stat, minWidth: 72 }}>
                        <span style={studio.statValue}>{counts.pending}</span>
                        <span style={studio.statLabel}>Pendientes</span>
                    </div>
                    <div style={{ ...studio.stat, minWidth: 72, borderLeftColor: ink.accent }}>
                        <span style={studio.statValue}>{counts.completed}</span>
                        <span style={studio.statLabel}>Por validar</span>
                    </div>
                    <div style={{ ...studio.stat, minWidth: 72, borderLeftColor: '#137333' }}>
                        <span style={studio.statValue}>{counts.validated}</span>
                        <span style={studio.statLabel}>Validados</span>
                    </div>
                </div>
            </div>

            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 12 }}>
                {[
                    { id: 'all', label: `Todos ${counts.total}` },
                    { id: 'pending', label: `Pendientes ${counts.pending}` },
                    { id: 'completed', label: `Por validar ${counts.completed}` },
                    { id: 'validated', label: `Validados ${counts.validated}` },
                    { id: 'mine', label: `Asignados a mí ${items.filter((item) => item.assignedTo?.uid === currentUser?.uid).length}` },
                ].map((tab) => {
                    const active = filter === tab.id;
                    return (
                        <button
                            key={tab.id}
                            type="button"
                            onClick={() => setFilter(tab.id)}
                            style={{
                                appearance: 'none',
                                borderWidth: 1,
                                borderStyle: 'solid',
                                borderColor: active ? ink.accent : ink.line,
                                backgroundColor: active ? ink.accentSoft : ink.surface,
                                color: active ? ink.accentText : ink.muted,
                                borderRadius: 999,
                                padding: narrow ? '7px 12px' : '3px 9px',
                                fontSize: narrow ? '0.8rem' : '0.72rem',
                                fontWeight: 700,
                                cursor: 'pointer',
                                fontFamily: 'inherit',
                                lineHeight: 1.4,
                            }}
                        >
                            {tab.label}
                        </button>
                    );
                })}
            </div>

            {error ? <p style={{ color: ink.danger, fontSize: '0.85rem', margin: '10px 0 0' }}>{error}</p> : null}

            <div style={{ display: 'flex', gap: 8, margin: '12px 0 14px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
                <textarea
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    placeholder="Nuevo ajuste…"
                    rows={2}
                    style={{ ...control, minHeight: 48, resize: 'vertical', flex: 1, minWidth: narrow ? '100%' : 220 }}
                />
                <label style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: narrow ? 0 : 240, flex: narrow ? 1 : undefined }}>
                    <span style={studio.hint}>Asignar a</span>
                    <FilterPill
                        value={assigneeId}
                        onChange={setAssigneeId}
                        emptyLabel={assigneeOptions.length ? 'Elige un responsable' : 'Sin responsables'}
                        style={{ minWidth: narrow ? 0 : 240, width: '100%', ...(narrow ? { fontSize: 16, minHeight: 40 } : null) }}
                    >
                        {assigneeOptions.map((row) => (
                            <option key={row.userId} value={row.userId}>{responsibleLabel(row)}</option>
                        ))}
                    </FilterPill>
                </label>
                <IconAction title="Crear ajuste" onClick={create} disabled={busy === 'create' || !assigneeOptions.length}>
                    <img src={SendIcon} alt="" style={iconImg} />
                </IconAction>
            </div>

            {visible.length === 0 ? (
                <div style={studio.empty}>No hay ajustes en este filtro.</div>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {visible.map((item) => {
                        const tone = STATUS_META[item.status] || STATUS_META.pending;
                        const canVal = item.status === 'completed' && canValidateItem(item);
                        return (
                            <div
                                key={item.id}
                                style={{
                                    padding: '10px 12px',
                                    borderRadius: 12,
                                    backgroundColor: ink.surface,
                                    borderWidth: 1,
                                    borderStyle: 'solid',
                                    borderColor: ink.line,
                                }}
                            >
                                <div style={{ display: 'flex', alignItems: narrow ? 'flex-start' : 'center', justifyContent: 'space-between', gap: 8, flexWrap: narrow ? 'wrap' : 'nowrap' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0, flexWrap: 'wrap', flex: narrow ? '1 1 180px' : undefined }}>
                                        <span style={{
                                            fontSize: '0.68rem',
                                            fontWeight: 700,
                                            letterSpacing: '0.05em',
                                            textTransform: 'uppercase',
                                            color: tone.color,
                                            backgroundColor: tone.backgroundColor,
                                            borderRadius: 999,
                                            padding: '2px 7px',
                                            flexShrink: 0,
                                        }}
                                        >
                                            {tone.label}
                                        </span>
                                        <span style={{ ...studio.hint, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: narrow ? 'normal' : 'nowrap' }}>
                                            {personName(item.requestedBy)} · {personMeta(item.requestedBy)}
                                            {item.assignedTo?.uid
                                                ? ` · asignado a ${personName(item.assignedTo)}`
                                                : ' · sin asignar'}
                                        </span>
                                    </div>
                                    <div style={{ display: 'flex', gap: narrow ? 6 : 4, flexShrink: 0, marginLeft: 'auto' }}>
                                        {canCompleteItem(item) && item.status === 'pending' && (
                                            <IconAction title="Completar" disabled={!!busy} onClick={() => complete(item.id)}>
                                                <CheckIcon />
                                            </IconAction>
                                        )}
                                        {canVal && (
                                            <IconAction title="Validar" disabled={!!busy} onClick={() => validate(item.id)}>
                                                <img src={UserCheckIcon} alt="" style={iconImg} />
                                            </IconAction>
                                        )}
                                        {canVal && (
                                            <IconAction title="No válido · reabrir" disabled={!!busy} onClick={() => { setReopenId(item.id); setReopenNote(''); }}>
                                                <img src={ReplyIcon} alt="" style={iconImg} />
                                            </IconAction>
                                        )}
                                        {canManage && item.status !== 'validated' && editingId !== item.id && (
                                            <IconAction title="Editar" onClick={() => { setEditingId(item.id); setEditText(item.text); }}>
                                                <img src={EditIcon} alt="" style={iconImg} />
                                            </IconAction>
                                        )}
                                        {editingId === item.id && (
                                            <>
                                                <IconAction title="Guardar" onClick={() => saveEdit(item.id)}>
                                                    <CheckIcon />
                                                </IconAction>
                                                <IconAction title="Cancelar" onClick={() => setEditingId(null)}>
                                                    <span style={{ fontSize: 16, lineHeight: 1, color: ink.muted }}>×</span>
                                                </IconAction>
                                            </>
                                        )}
                                        {canManage && (
                                            <IconAction title="Eliminar" danger disabled={!!busy} onClick={() => remove(item.id)}>
                                                <img src={TrashIcon} alt="" style={iconImg} />
                                            </IconAction>
                                        )}
                                    </div>
                                </div>
                                {editingId === item.id ? (
                                    <textarea value={editText} onChange={(e) => setEditText(e.target.value)} style={{ ...control, marginTop: 8 }} rows={2} />
                                ) : (
                                    <AdjustmentBody
                                        text={item.text}
                                        expanded={expandedIds.has(item.id)}
                                        onToggle={() => toggleExpanded(item.id)}
                                    />
                                )}
                                {canAssignItem(item) && item.status !== 'validated' && assigneeOptions.length > 0 && (
                                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
                                        <span style={studio.hint}>Reasignar</span>
                                        <FilterPill
                                            value={item.assignedTo?.uid || ''}
                                            onChange={(next) => reassign(item.id, next)}
                                            emptyLabel={item.assignedTo?.uid ? personName(item.assignedTo) : 'Sin asignar'}
                                            disabled={!!busy}
                                            style={narrow ? { minWidth: 0, flex: 1, fontSize: 16, minHeight: 40 } : { minWidth: 220 }}
                                        >
                                            {assigneeOptions.map((row) => (
                                                <option key={row.userId} value={row.userId}>{responsibleLabel(row)}</option>
                                            ))}
                                        </FilterPill>
                                    </label>
                                )}
                                {item.reopenNote ? <p style={{ ...studio.hint, marginTop: 6 }}>Nota: {item.reopenNote}</p> : null}
                                {reopenId === item.id && (
                                    <div style={{ marginTop: 8, display: 'flex', gap: 6, alignItems: 'flex-end', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                                        <textarea value={reopenNote} onChange={(e) => setReopenNote(e.target.value)} placeholder="Nota opcional" style={{ ...control, flex: 1, minWidth: narrow ? '100%' : 0 }} rows={2} />
                                        <IconAction title="Reabrir" onClick={() => reopen(item.id)}>
                                            <CheckIcon />
                                        </IconAction>
                                        <IconAction title="Cancelar" onClick={() => setReopenId(null)}>
                                            <span style={{ fontSize: 16, lineHeight: 1, color: ink.muted }}>×</span>
                                        </IconAction>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export const statsForRequester = (adjustments, userId) => {
    const mine = (adjustments || []).filter((item) => item.requestedBy?.uid === userId);
    return {
        requested: mine.length,
        completed: mine.filter((item) => item.status === 'completed' || item.status === 'validated').length,
    };
};

export const statsForAssignee = (adjustments, userId) => {
    const mine = (adjustments || []).filter((item) => item.assignedTo?.uid === userId);
    const now = Date.now();
    const pending = mine.filter((item) => item.status === 'pending');
    const open = mine.filter((item) => item.status === 'pending' || item.status === 'completed');
    const resolved = mine.filter((item) => item.status === 'completed' || item.status === 'validated');
    const ignored = pending.filter((item) => {
        const start = toMillis(item.assignedAt) || toMillis(item.createdAt);
        return start && (now - start) > STALE_MS;
    });
    return {
        assigned: mine.length,
        pending: pending.length,
        open: open.length,
        ignored: ignored.length,
        resolved: resolved.length,
    };
};

export default ReportAdjustmentsPanel;
